/**
 * Shared helpers and config for Bond Retrieval guardian forms.
 */

import {
  HISTORIC_GUARDIAN_BOND,
  HISTORIC_GUARDIAN_CONFIG,
  HISTORIC_GUARDIAN_FIELD_KEYS,
} from "../Bond Creation Historic/historicGuardianConfig";
import {
  mapProfessionalGuardianGuardianship,
  mapProfessionalGuardianProbate,
  mapProfessionalGuardianTrustee,
  mapProfessionalGuardianVA,
} from "./hooks/bondRetrievalGuardianMappers";

/** Mirror historic kind ids — same field-key layout for principals. */
export const RETRIEVAL_GUARDIAN_BOND = HISTORIC_GUARDIAN_BOND;

/**
 * Spread historic UI/metadata for parity; retrieval overrides mappers/law firm / fetchPg email.
 *
 * @typedef {object} RetrievalGuardianConfigEntry
 * @property {typeof HISTORIC_GUARDIAN_FIELD_KEYS[string]} fieldKeys
 * @property {(g: object) => object} mapProfessionalGuardian
 * @property {boolean} useReduxAttorneyLawFirmId
 * @property {'GuardianNumber'|'PrincipalNumber'} guardianCountKey
 * @property {boolean} fetchPgIncludesEmail
 * @property {boolean} enablePgNameSuggestion
 * @property {string} pgAddressOptionKey — PG autocomplete option property for Address row in card (`address` or `guardian_street_address_1`)
 * @property {boolean} manualEmailBanner — EmailRequiredInfo box when email step shows
 * @property {string} attorneyHomeToggleLabel — copy for attorney home-address yes/no row
 * @property {string} attorneyAutocompleteLabel — autocomplete label (“Search Attorney…”)
 * @property {'Selected Attorney Details'|'Selected Attorney'} attorneyCardSubtitle
 * @property {'isDisabled'|'disabled'} labeledSelectDisabledPropName — Probates Creatable passes `isDisabled`
 * @property {string} probateAttorneyHomeGoogleLabel — Probate Attorney home-only Google wrapper label prop
 */

/**
 * Base UI from historic guardian config keyed by bond type.
 */
function retrievalUiOverlay(kind, overlay) {
  const h = HISTORIC_GUARDIAN_CONFIG[kind];
  return {
    bondType: kind,
    emailFieldLabel: h.emailFieldLabel,
    zipLookup: h.zipLookup,
    pgLookupBoxSx: h.pgLookupBoxSx,
    pgCardRows: [...h.pgCardRows],
    attorneyUi: h.attorneyUi,
    emailUi: h.emailUi,
    bondAmountSsnOp: h.bondAmountSsnOp,
    showManualOccupation: h.showManualOccupation,
    manualOccupationWhen: h.manualOccupationWhen,
    relationshipLabel: h.relationshipLabel,
    relationshipRequired: h.relationshipRequired,
    relationshipShowWhen: h.relationshipShowWhen,
    googlePlaceholderManual: h.googlePlaceholderManual,
    manualStyles: h.manualStyles,
    enablePgNameSuggestion: false,
    labeledSelectDisabledPropName: overlay.labeledSelectDisabledPropName ?? "disabled",
    ...overlay,
  };
}

/** @type {Record<string, object>} */
export const RETRIEVAL_GUARDIAN_CONFIG = {
  [RETRIEVAL_GUARDIAN_BOND.guardianship]: retrievalUiOverlay(
    RETRIEVAL_GUARDIAN_BOND.guardianship,
    {
      fieldKeys: HISTORIC_GUARDIAN_FIELD_KEYS[RETRIEVAL_GUARDIAN_BOND.guardianship],
      mapProfessionalGuardian: mapProfessionalGuardianGuardianship,
      useReduxAttorneyLawFirmId: true,
      guardianCountKey: "GuardianNumber",
      fetchPgIncludesEmail: false,
      pgAddressOptionKey: "guardian_street_address_1",
      manualEmailBanner: false,
      attorneyHomeToggleLabel: "Use attorney's home address?",
      attorneyAutocompleteLabel: "Search Attorney",
      attorneyCardSubtitle: "Selected Attorney Details",
      pgLookupOuterSx: { mt: 4 },
    }
  ),

  [RETRIEVAL_GUARDIAN_BOND.probate]: retrievalUiOverlay(
    RETRIEVAL_GUARDIAN_BOND.probate,
    {
      fieldKeys: HISTORIC_GUARDIAN_FIELD_KEYS[RETRIEVAL_GUARDIAN_BOND.probate],
      mapProfessionalGuardian: mapProfessionalGuardianProbate,
      useReduxAttorneyLawFirmId: false,
      guardianCountKey: "PrincipalNumber",
      fetchPgIncludesEmail: true,
      pgAddressOptionKey: "address",
      manualOccupationWhen: "highBondNotPgNotAttorney",
      manualEmailBanner: false,
      attorneyHomeToggleLabel: "Is this Attorney's Home Address?",
      attorneyAutocompleteLabel: "Search Attorney",
      attorneyCardSubtitle: "Selected Attorney",
      labeledSelectDisabledPropName: "isDisabled",
      probateAttorneyHomeGoogleLabel: "Search Address",
      pgLookupOuterSx: {},
    }
  ),

  [RETRIEVAL_GUARDIAN_BOND.trustee]: retrievalUiOverlay(
    RETRIEVAL_GUARDIAN_BOND.trustee,
    {
      fieldKeys: HISTORIC_GUARDIAN_FIELD_KEYS[RETRIEVAL_GUARDIAN_BOND.trustee],
      mapProfessionalGuardian: mapProfessionalGuardianTrustee,
      useReduxAttorneyLawFirmId: false,
      guardianCountKey: "PrincipalNumber",
      fetchPgIncludesEmail: false,
      pgAddressOptionKey: "address",
      manualOccupationWhen: "highBondNotPgNotAttorney",
      manualEmailBanner: true,
      attorneyHomeToggleLabel: "Use attorney's home address?",
      attorneyAutocompleteLabel: "Search Attorney",
      attorneyCardSubtitle: "Selected Attorney Details",
      pgLookupOuterSx: { mt: 4 },
    }
  ),

  [RETRIEVAL_GUARDIAN_BOND.va]: retrievalUiOverlay(RETRIEVAL_GUARDIAN_BOND.va, {
    fieldKeys: HISTORIC_GUARDIAN_FIELD_KEYS[RETRIEVAL_GUARDIAN_BOND.va],
    mapProfessionalGuardian: mapProfessionalGuardianVA,
    useReduxAttorneyLawFirmId: false,
    guardianCountKey: "PrincipalNumber",
    fetchPgIncludesEmail: false,
    pgAddressOptionKey: "address",
    manualOccupationWhen: "highBondNotPgNotAttorney",
    showManualOccupation: true,
    manualEmailBanner: true,
    attorneyHomeToggleLabel: "",
    attorneyAutocompleteLabel: "Search Attorney",
    attorneyCardSubtitle: "Selected Attorney",
    pgLookupOuterSx: { mt: 4 },
  }),
};

/**
 * Law-firm id for `/lawfirm-attorneys/:id` when loading attorney dropdowns.
 * @param {string} userRole
 * @param {{ lawfirmId?: number }} lawfirm
 * @returns {number}
 */
export function getRetrievalAttorneyLawFirmId(userRole, lawfirm) {
  if (userRole === "law_firm_user" || userRole === "law_firm_admin") {
    return lawfirm?.lawfirmId ?? 0;
  }
  return 0;
}
